import {observable, computed, action, makeObservable} from 'mobx';
import Cookies from 'universal-cookie';

import {Form} from './Form';
import {SearchesStore} from '../../Searches/stores/SearchesStore';
import {
    serialize,
    getSession,
    serializeResultTables,
    serializeActivations,
    serializeStatuses,
    serializeMacros
} from '../serializers/form';
import {serialize as serializeConstants} from '../serializers/constants';
import {serializeQueryFilters} from '../../Searches/serializes/searchesList';
import {debugServerSessionSuffix} from '../consts/form';
import {ViewType} from '../consts/viewTypes';
import {Constant, Rt, NewTable, DataSetForm, Form as FormType, MacroData} from '../types';
import {handleMoreInfoPersonalization} from '../../../features/Personalization/utils/personalization';
import {ScreenData} from '../../../services/indexDB/indexeddbServiceZoomedScreens';
import {systemLang} from '../../../Localization/languages';
import {getDname} from '../../../_shared/GWTBridge/invoke/genPage';

export class FormsStore {
    systemLangs: systemLang[];
    @observable sessions: Map<string, {forms: Map<string, Form>}>;
    @observable currentSession: string;
    @observable currentFormName: string;
    @observable savingStatus: number;
    @observable currentRecordingMacro: MacroData;

    moreInfoForms = {};
    moreInfoCols = {};
    prevIndexedScreens = {};

    constructor() {
        makeObservable(this);
        this.sessions = new Map();
    }

    @computed get currentForm(): Form {
        const session = this.sessions.get(this.currentSession);
        const currentForm = session?.forms.get(this.currentFormName);

        return currentForm;
    }

    setMoreInfoCachedCol(key: string, col: any) {
        const cookies = new Cookies();
        const companyName = cookies.get('Dname');
        this.moreInfoCols[`${key}_${companyName}`] = col;
    }

    getMoreInfoCachedCol(key: string) {
        const cookies = new Cookies();
        const companyName = cookies.get('Dname');
        return this.moreInfoCols[`${key}_${companyName}`];
    }

    setMoreInfoPrevIndexed(key: string, data: ScreenData[]) {
        this.prevIndexedScreens[key] = data;
    }

    getMoreInfoPrevIndexed(key: string) {
        return this.prevIndexedScreens[key];
    }

    getFormFromSession(session: string, name: string): Form {
        const formSession = this.sessions.get(session);
        return formSession && formSession.forms.get(name);
    }

    getForm(tableName): Form {
        const session = this.sessions.get(this.currentSession);
        return session && session.forms.get(tableName);
    }

    getFormByLevel(tableLevel: string, session: {forms: Map<string, Form>}): Form {
        for (const form of session.forms.values()) {
            if (form.table === tableLevel) {
                return form;
            }
        }
        return null;
    }

    setCurrentRecordingMacro(macro: MacroData) {
        this.currentRecordingMacro = macro;
    }

    @action setForms(data: DataSetForm) {
        const session = getSession(data);

        if (session) {
            const form: FormType = data.Form[0];
            const forms: Array<NewTable> = form.NewTable;
            const Constants: Array<Constant> = form.Constants;
            const RT: Array<Rt> = form.RT;

            const resultTables = serializeResultTables(RT);
            const constants = serializeConstants(Constants);

            let macros = serializeMacros(form);

            const formCompanyName = getDname();

            for (const formIndex in forms) {
                const newTable: NewTable = forms[formIndex];
                const form = serialize(newTable);
                let formData;
                if (formIndex === '0') {
                    const {hasAttachment, isPainted, name, table, title, level} = form;
                    formData = {hasAttachment, isPainted, name, table, title, level};
                }
                const activations = serializeActivations(newTable.Activation);
                const defaultSearch = serializeQueryFilters(form, data);
                const sessionToUpdate = this.sessions.get(session);
                const resultTable = resultTables?.get(Number(formIndex) + 1 + '');
                const statuses = serializeStatuses(newTable.Statuses);

                if (sessionToUpdate) {
                    const formToUpdate = sessionToUpdate.forms.get(form.name);
                    if (formToUpdate && formToUpdate.table === form.table) {
                        formToUpdate.formTable.updateFormColumns(
                            form.columnsMap,
                            form.zoomField,
                            form.isPainted
                        );
                    } else {
                        const sameLevelForm = this.getFormByLevel(form.table, sessionToUpdate);
                        sameLevelForm && sessionToUpdate.forms.delete(sameLevelForm.name);
                        const formLevel = parseInt(form.table);
                        let parentFormName = null;
                        if (formLevel > 1) {
                            parentFormName = this.getFormByLevel(
                                (formLevel - 1).toString(),
                                sessionToUpdate
                            )?.name;
                            const mainForm = this.getFormByLevel('1', sessionToUpdate);
                            macros = mainForm.macros;
                        }
                        sessionToUpdate.forms.set(
                            form.name,
                            new Form(
                                this,
                                form,
                                constants,
                                resultTable,
                                newTable,
                                activations,
                                defaultSearch,
                                session,
                                parentFormName,
                                statuses,
                                macros,
                                formCompanyName
                            )
                        );
                    }
                } else {
                    this.sessions.set(session, {forms: new Map()});
                    this.sessions
                        .get(session)
                        .forms.set(
                            form.name,
                            new Form(
                                this,
                                form,
                                constants,
                                resultTable,
                                newTable,
                                activations,
                                defaultSearch,
                                session,
                                null,
                                statuses,
                                macros,
                                formCompanyName
                            )
                        );
                }
                formData && handleMoreInfoPersonalization(formData);
            }
        }
    }

    // when exiting a form we should clear the form and its subforms
    @action clearForm(session) {
        this.sessions.delete(session);
    }

    @action setSavingStatus(status) {
        this.savingStatus = status;
    }

    @computed get isReactView() {
        return this.currentForm && this.currentForm.formType === ViewType.RECON;
    }

    @action clearForms() {
        this.sessions.clear();
        this.currentFormName = undefined;
        this.currentSession = undefined;
    }

    @action setCurrentForm(session: string, name: string) {
        if (session && session.endsWith(debugServerSessionSuffix)) {
            this.currentSession = session.substring(
                0,
                session.length - debugServerSessionSuffix.length
            );
        } else {
            this.currentSession = session;
        }
        this.currentFormName = name;
    }

    @computed get searchesStore() {
        return this.currentForm && this.currentForm.searches
            ? this.currentForm.searches
            : ({} as SearchesStore);
    }

    @action resetFilters(session: string, name: string) {
        if (session === formsStore.currentSession) {
            const currentForm = formsStore.currentForm;
            const targetForm = formsStore.getForm(name);
            if (
                currentForm &&
                targetForm &&
                (targetForm.table === currentForm.table || targetForm.table < currentForm.table)
            ) {
                formsStore.currentForm?.searches?.resetToDefault();
                if (currentForm.parentForm && currentForm.parentForm !== targetForm) {
                    let nextParent = currentForm.parentForm;
                    while (nextParent.table > targetForm.table) {
                        nextParent?.searches?.resetToDefault();
                        nextParent = nextParent.parentForm;
                    }
                }
            }
        }
    }
}

export const formsStore = new FormsStore();
